import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

//Custom Components
import * as baseAction from "store/base/base.action";
import * as tagsAction from "store/tags/tags.action";
import * as domainsAction from "store/domains/domains.action";
import * as localesAction from "store/locales/locales.action";
import * as timezonesAction from "store/timezones/timezones.action";
import InputField from "components/input-field/input-field.component";
import DropDownSearch from "components/form/dropdown-search/dropdown-search.component";
import Tag from "components/form/dropdown-search/ dropdown-search-tag/dropdown-search-tag.component";
import Button from "components/form/buttons/button/button.component";
import TextInput from "components/form/inputs/text/text-input.component";

// Styles
import "./customer-info.styles.scss";

const CustomerInfo = ({
  customer,
  setCustomer,
  setShowAddContactPersonModal,
  handleRemoveContactPerson,
  onContinueToSubscription
}) => {
  const dispatch = useDispatch();
  const [tag, setTagName] = useState();
  const [filter, setFilter] = useState({});
  const [showDropdown, setShowDropdown] = useState(false);
  const [localErrors, setLocalErrors] = useState([]);

  // Load the initial customer details in the local store
  useEffect(() => {
    dispatch(tagsAction.getTags(filter));
    dispatch(timezonesAction.getTimezones(filter));
    dispatch(localesAction.getLocales(filter));
    dispatch(domainsAction.getDomains(filter));
    // eslint-disable-next-line
  }, []);

  const tags = useSelector(state => state.tags[tagsAction.GET_TAGS]);
  const getTagsError = useSelector(state => state.tags[tagsAction.TAGS_ERRORS]);
  const isTagsLoading = useSelector(
    state => state.tags[tagsAction.TAGS_LOADING]
  );
  const addTagsResponse = useSelector(state => state.tags[tagsAction.ADD_TAGS]);
  const timezones = useSelector(
    state => state.timezones[timezonesAction.GET_TIMEZONES]
  );
  const isTimezonesLoading = useSelector(
    state => state.timezones[timezonesAction.TIMEZONES_LOADING]
  );
  const domains = useSelector(
    state => state.domains[domainsAction.GET_DOMAINS]
  );
  const isDomainsLoading = useSelector(
    state => state.domains[domainsAction.DOMAINS_LOADING]
  );
  const locales = useSelector(
    state => state.locales[localesAction.GET_LOCALES]
  );
  const isLocalesLoading = useSelector(
    state => state.locales[localesAction.LOCALES_LOADING]
  );

  const combinedErrors = localErrors;

  const isValidEmail = value => {
    const emailRegex =
      /^(?:[a-zA-Z0-9_'^&\/+\-])+(?:\.(?:[a-zA-Z0-9_'^&\/+\-])+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
    return emailRegex.test(String(value || "").trim());
  };

  const isValidSubdomain = value => {
    // Mirrors backend: /^(?![-.])[a-zA-Z0-9.-]+(?<![-.])$/
    const subdomainRegex = /^(?![-.])[a-zA-Z0-9.-]+(?<![-.])$/;
    return subdomainRegex.test(String(value || "").trim());
  };

  const buildError = (field, message) => ({ field, message });
  const fieldLabels = {
    company_full_legal_name: "Company full legal name",
    company_display_name: "Company display name",
    customer_domain: "Customer subdomain",
    domain: "Domain",
    primary_admin_email: "Primary admin email",
    primary_admin_firstname: "Primary admin first name",
    primary_admin_lastname: "Primary admin last name"
  };
  // On input change: clear any existing local error for that field.
  const clearLocalError = fieldId =>
    setLocalErrors(prev => prev.filter(err => err.field !== fieldId));

  const validateBeforeSubmit = () => {
    const requiredChecksInOrder = [
      {
        cond: !customer?.company_full_legal_name,
        field: "company_full_legal_name"
      },
      { cond: !customer?.company_display_name, field: "company_display_name" },
      { cond: !customer?.subdomain, field: "customer_domain" },
      { cond: !customer?.domain, field: "domain" },
      { cond: !customer?.primary_admin_email, field: "primary_admin_email" },
      {
        cond: !customer?.primary_admin_firstname,
        field: "primary_admin_firstname"
      },
      {
        cond: !customer?.primary_admin_lastname,
        field: "primary_admin_lastname"
      }
    ];

    const firstRequiredFailure = requiredChecksInOrder.find(
      check => check.cond
    );
    if (firstRequiredFailure) {
      const label = fieldLabels[firstRequiredFailure.field] || "This field";
      setLocalErrors([
        buildError(firstRequiredFailure.field, `${label} is required`)
      ]);
      return false;
    }

    // Format validations (run only if requireds pass)
    if (!isValidEmail(customer.primary_admin_email)) {
      setLocalErrors([
        buildError("primary_admin_email", "Email address is invalid format")
      ]);
      return false;
    }
    if (!isValidSubdomain(customer.subdomain)) {
      setLocalErrors([buildError("customer_domain", "Must be a valid URL")]);
      return false;
    }

    setLocalErrors([]);
    return true;
  };

  const handleContinue = () => {
    if (validateBeforeSubmit()) {
      onContinueToSubscription();
    }
  };

  const handleAddTag = tag => {
    if (!tag) return;
    // Check if the tag already exists
    const tagExists = customer?.tags?.some(t => t.name === tag.name);
    if (tagExists) return;

    setCustomer({
      ...customer,
      tags: [...customer?.tags, tag]
    });
    setShowDropdown(false);
  };

  const handleRemoveTag = tag => {
    if (!tag) return;
    const newTags = customer?.tags?.filter(
      tagItem => tagItem.name !== tag.name
    );
    setCustomer({
      ...customer,
      tags: newTags
    });
  };

  const addNewTag = async value => {
    dispatch(tagsAction.addTags([{ name: value }]));
    setTagName(value);
    setShowDropdown(false);
  };

  useEffect(() => {
    if (addTagsResponse?.notifications) {
      handleAddTag({
        id: addTagsResponse?.data?.upserted[0]?.id,
        name: tag
      });
      dispatch(baseAction.reset(tagsAction.ADD_TAGS));
    }
  }, [addTagsResponse]);

  const handleChange = e => {
    const targetValue = e.target.value;
    setFilter(prevState => ({
      ...prevState,
      name: targetValue
    }));
    dispatch(tagsAction.getTags(filter));
    if (e.key === "Enter" && e.target.value.length !== 0) {
      addNewTag(e.target.value);
    }
    e.target.value = "";
  };

  return (
    <>
      {/* Contents */}
      <div className="customer">
        <div className="input-group mb-20">
          <div className="input-group mb-20">
            <label>
              Company Full Legal name
              <span className="text-danger">*</span>
            </label>
            <TextInput
              type="text"
              id="company_full_legal_name"
              name="company_full_legal_name"
              className="input-xxl pl-25"
              errors={combinedErrors}
              iconClass="far fa-building"
              iconPosition="left"
              value={customer?.company_full_legal_name || ""}
              onChange={e => {
                const targetValue = e.target.value;
                setCustomer(prevState => ({
                  ...prevState,
                  company_full_legal_name: targetValue
                }));
                clearLocalError("company_full_legal_name");
              }}
            />
          </div>
          <div className="input-group mb-20">
            <label>
              Company Display Name
              <span className="text-danger">*</span>
            </label>
            <TextInput
              type="text"
              id="company_display_name"
              name="company_display_name"
              className="input-xxl pl-25"
              errors={combinedErrors}
              iconClass="far fa-building"
              iconPosition="left"
              value={customer?.company_display_name || ""}
              onChange={e => {
                const targetValue = e.target.value;
                setCustomer(prevState => ({
                  ...prevState,
                  company_display_name: targetValue
                }));
                clearLocalError("company_display_name");
              }}
            />
          </div>
          <div className="input-group mb-20">
            <label>
              Customer Subdomain
              <span className="text-danger">*</span>
            </label>
            <div className="customer__product-type">
              <div className="input-items">
                <TextInput
                  type="text"
                  id="customer_domain"
                  name="customer_domain"
                  className="input-xxl pl-25"
                  errors={combinedErrors}
                  iconClass="fa fa-server"
                  iconPosition="left"
                  value={customer?.subdomain || ""}
                  onChange={e => {
                    const subdomain = e.target.value;
                    setCustomer(prevState => ({
                      ...prevState,
                      subdomain
                    }));
                    clearLocalError("customer_domain");
                  }}
                />
                <div className="text-muted">Cannot be changed later</div>
              </div>

              <div className="input-items">
                <DropDownSearch
                  title={(() => {
                    let title = "Domain";
                    if (customer?.domain) {
                      title = customer?.domain;
                    }
                    return title;
                  })()}
                  options={domains?.data?.map(domain => ({
                    id: domain.id,
                    label: domain.id,
                    value: domain.id,
                    data: domain
                  }))}
                  onSelect={item => {
                    const targetValue = item.value;
                    setCustomer(prevState => ({
                      ...prevState,
                      domain: targetValue
                    }));
                    setLocalErrors(prev =>
                      prev.filter(err => err.field !== "domain")
                    );
                  }}
                  showSearch={false}
                  errorField="domain"
                  errors={combinedErrors}
                />
              </div>
            </div>
          </div>
          <div className="input-group mb-20">
            <label>
              Primary Admin Email
              <span className="text-danger">*</span>
            </label>
            <div className="customer__dms-name">
              <TextInput
                type="text"
                id="primary_admin_email"
                name="primary_admin_email"
                className="input-xxl pl-25"
                errors={combinedErrors}
                iconClass="far fa-envelope"
                iconPosition="left"
                value={customer?.primary_admin_email || ""}
                onChange={e => {
                  const targetValue = e.target.value;
                  setCustomer(prevState => ({
                    ...prevState,
                    primary_admin_email: targetValue
                  }));
                  clearLocalError("primary_admin_email");
                }}
              />
              <div className="text-muted">
                Primary admin password will be autogenerated & emailed to user
              </div>
            </div>
          </div>
          <div className="input-group mb-20">
            <label>
              Primary Admin First Name
              <span className="text-danger">*</span>
            </label>
            <div className="customer__organisation">
              <TextInput
                type="text"
                id="primary_admin_firstname"
                name="primary_admin_firstname"
                className="input-xxl pl-25"
                errors={combinedErrors}
                iconClass="far fa-user"
                iconPosition="left"
                value={customer?.primary_admin_firstname || ""}
                onChange={e => {
                  const targetValue = e.target.value;
                  setCustomer(prevState => ({
                    ...prevState,
                    primary_admin_firstname: targetValue
                  }));
                  clearLocalError("primary_admin_firstname");
                }}
              />
            </div>
          </div>
          <div className="input-group mb-20">
            <label>
              Primary Admin Last Name
              <span className="text-danger">*</span>
            </label>
            <div className="customer__website">
              <TextInput
                type="text"
                id="primary_admin_lastname"
                name="primary_admin_lastname"
                className="input-xxl pl-25"
                errors={combinedErrors}
                iconClass="far fa-user"
                iconPosition="left"
                value={customer?.primary_admin_lastname || ""}
                onChange={e => {
                  const targetValue = e.target.value;
                  setCustomer(prevState => ({
                    ...prevState,
                    primary_admin_lastname: targetValue
                  }));
                  clearLocalError("primary_admin_lastname");
                }}
              />
            </div>
          </div>
        </div>
        <div className="input-group mb-20">
          <label>Default Time Zone</label>
          <DropDownSearch
            title={(() => {
              let title = "Default Time Zone";
              if (customer?.default_time_zone) {
                title = customer.default_time_zone;
              }
              return title;
            })()}
            onSearch={value =>
              dispatch(timezonesAction.getTimezones({ name: value }))
            }
            onBlur={() => dispatch(timezonesAction.getTimezones({}))}
            options={timezones?.data?.map(timezone => ({
              id: timezone.id,
              label: timezone.name,
              value: timezone.name,
              data: timezone
            }))}
            onSelect={item => {
              const targetValue = item.value;
              setCustomer(prevState => ({
                ...prevState,
                default_time_zone: targetValue
              }));
              setLocalErrors(prev =>
                prev.filter(err => err.field !== "default_time_zone")
              );
            }}
            showSearch={true}
            errorField="default_time_zone"
            errors={combinedErrors}
          />
        </div>

        <div className="input-group mb-20">
          <label>Default Language</label>
          <DropDownSearch
            title={(() => {
              let title = "Default Language";
              if (customer?.default_language) {
                title = customer.default_language;
              }
              return title;
            })()}
            onSearch={value =>
              dispatch(localesAction.getLocales({ name: value }))
            }
            onBlur={() => dispatch(localesAction.getLocales({}))}
            options={locales?.data?.map(locale => ({
              id: locale.id,
              label: locale.name,
              value: locale.name,
              data: locale
            }))}
            onSelect={item => {
              const targetValue = item.value;
              setCustomer(prevState => ({
                ...prevState,
                default_language: targetValue
              }));
              setLocalErrors(prev =>
                prev.filter(err => err.field !== "default_language")
              );
            }}
            errorField="default_language"
            errors={combinedErrors}
          />
        </div>

        <div className="input-group mb-20">
          <label>Tags</label>
          <TextInput
            type="text"
            id="tags"
            name="primary_admin_lastname"
            className="input-xxl pl-25"
            errors={combinedErrors}
            placeholder="Add Tags..."
            onKeyUp={handleChange}
            onChange={() => setShowDropdown(true)}
            autoComplete="off"
          />
          {showDropdown ? (
            <div className="add__customer-dropdown-container">
              {tags?.data &&
                tags?.data.map(item => (
                  <li
                    key={item.id}
                    className="add__customer-dropdown-list-item"
                    onClick={() => handleAddTag(item)}
                  >
                    {item.name}
                  </li>
                ))}
            </div>
          ) : null}
        </div>

        {customer?.tags?.length > 0 && (
          <div className="app__user--add-tags mt-20 mb-20">
            {customer?.tags?.map(tag => (
              <Tag
                key={tag?.id}
                label={tag?.name}
                tag={tag}
                onRemove={handleRemoveTag}
                wrapperClass="mr-10"
              />
            ))}
          </div>
        )}
        <div className="input-group mb-20">
          <label>Contact Persons</label>
          <InputField
            setShowAddContactPersonModal={setShowAddContactPersonModal}
            customer={customer}
            handleRemoveContactPerson={handleRemoveContactPerson}
            errors={combinedErrors}
            onFocus={setShowAddContactPersonModal}
          />
        </div>
      </div>

      {/* End Body */}
      <div className="row justify-center mb-40 mt-40">
        <Button
          id="intro"
          className="btn btn--info"
          onClick={handleContinue}
        >
          Add Subscription and Billing Information
        </Button>
      </div>
    </>
  );
};

CustomerInfo.propTypes = {
  customer: PropTypes.object.isRequired,
  setCustomer: PropTypes.func.isRequired,
  setShowAddContactPersonModal: PropTypes.func.isRequired,
  handleRemoveContactPerson: PropTypes.func.isRequired,
  onContinueToSubscription: PropTypes.func.isRequired
};

export default CustomerInfo;
